import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import { Archive, ArrowLeft, GraduationCap, Pencil, Plus, RotateCcw } from 'lucide-react'
import {
  getV1MeRetrieveQueryOptions,
  getV1SchoolsListQueryKey,
  getV1SchoolsRetrieveQueryKey,
  useV1MeRetrieve,
  useV1SchoolsAnonymizeCreate,
  useV1SchoolsArchiveCreate,
  useV1SchoolsBulkDisableCreate,
  useV1SchoolsRestoreCreate,
  useV1SchoolsRetrieve,
} from '@/api/client'
import type { StateEnum } from '@/api/model'
import { OrdinalEnum } from '@/api/model'
import { PageHeader } from '@/components/page-header'
import { SchoolPendingProposals } from '@/components/schools/school-pending-proposals'
import { SchoolProposalHistory } from '@/components/schools/school-proposal-history'
import { RspoZespolenie } from '@/components/schools/rspo-zespolenie'
import { StatusBadge, type StatusTone } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ConfirmDialog } from '@/components/admin-accounts/confirm-dialog'
import { BulkScopeActions } from '@/components/bulk/bulk-scope-actions'
import { ClassForm } from '@/components/classes/class-form'
import { ModuleConfigCard } from '@/components/modules/module-config-card'
import { LogoUpload } from '@/components/schools/logo-upload'
import { SchoolForm } from '@/components/schools/school-form'
import { SchoolTeachersCard } from '@/components/teachers/school-teachers-card'

export const Route = createFileRoute('/_panel/szkoly/$szkolaId')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role === 'teacher') {
      throw redirect({ to: '/' })
    }
  },
  component: SchoolDetailPage,
})

const stateTone: Record<StateEnum, StatusTone> = {
  active: 'green',
  archived: 'zinc',
  deleted: 'red',
}

function SchoolDetailPage() {
  const { szkolaId } = Route.useParams()
  const id = Number(szkolaId)
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [creatingClass, setCreatingClass] = useState(false)
  const [lifecycle, setLifecycle] = useState<null | 'archive' | 'restore'>(null)
  const [lifecycleError, setLifecycleError] = useState<string | null>(null)
  const query = useV1SchoolsRetrieve(id)
  const school = query.data?.status === 200 ? query.data.data : undefined

  const meQuery = useV1MeRetrieve()
  const role = meQuery.data?.status === 200 ? meQuery.data.data.role : undefined
  const isSuperadmin = role === 'superadmin'
  const isOperator = role === 'superadmin' || role === 'admin'

  const archiveMutation = useV1SchoolsArchiveCreate()
  const restoreMutation = useV1SchoolsRestoreCreate()
  const bulkDisableMutation = useV1SchoolsBulkDisableCreate()
  const anonymizeMutation = useV1SchoolsAnonymizeCreate()
  const pending = archiveMutation.isPending || restoreMutation.isPending

  const runLifecycle = async () => {
    setLifecycleError(null)
    const mutation = lifecycle === 'archive' ? archiveMutation : restoreMutation
    const response = await mutation.mutateAsync({ id })
    if (response.status >= 400) {
      setLifecycleError('Nie udało się zmienić stanu szkoły. Spróbuj ponownie.')
      return
    }
    await queryClient.invalidateQueries({ queryKey: getV1SchoolsRetrieveQueryKey(id) })
    await queryClient.invalidateQueries({ queryKey: getV1SchoolsListQueryKey() })
    setLifecycle(null)
  }

  if (query.isLoading) {
    return (
      <>
        <PageHeader title="Szkoła" />
        <p className="p-6 text-sm text-muted-foreground">Ładowanie…</p>
      </>
    )
  }

  if (!school) {
    return (
      <>
        <PageHeader title="Szkoła" />
        <p className="p-6 text-sm text-muted-foreground">Nie znaleziono szkoły.</p>
      </>
    )
  }

  if (editing) {
    return (
      <>
        <PageHeader
          title={
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="size-4" /> {school.name}
            </button>
          }
        />
        <div className="p-6">
          <h2 className="mb-6 text-lg font-semibold">Edycja szkoły</h2>
          <SchoolForm school={school} />
        </div>
      </>
    )
  }

  if (creatingClass) {
    return (
      <>
        <PageHeader
          title={
            <button
              type="button"
              onClick={() => setCreatingClass(false)}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="size-4" /> {school.name}
            </button>
          }
        />
        <div className="p-6">
          <h2 className="mb-6 text-lg font-semibold">Nowa klasa w szkole {school.name}</h2>
          <ClassForm defaultSchool={school.id} />
        </div>
      </>
    )
  }

  const contact1 = school.contacts.find((c) => c.ordinal === OrdinalEnum.NUMBER_1)
  const contact2 = school.contacts.find((c) => c.ordinal === OrdinalEnum.NUMBER_2)

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <Button asChild variant="ghost" size="icon-sm">
              <Link to="/szkoly">
                <ArrowLeft />
              </Link>
            </Button>
            {school.name}
            <StatusBadge tone={stateTone[school.state]}>{school.state_label}</StatusBadge>
          </span>
        }
        actions={
          <div className="flex gap-2">
            {/* Establish a class in this school (TR-600, launched from the school
                detail — spec 012 FR-003). Only an active school can host new classes. */}
            {school.state === 'active' && (
              <Button size="sm" variant="outline" onClick={() => setCreatingClass(true)}>
                <Plus /> Załóż klasę
              </Button>
            )}
            {school.state === 'active' && (
              <Button size="sm" variant="outline" onClick={() => setLifecycle('archive')}>
                <Archive /> Archiwizuj
              </Button>
            )}
            {school.state === 'archived' && (
              <Button size="sm" variant="outline" onClick={() => setLifecycle('restore')}>
                <RotateCcw /> Przywróć
              </Button>
            )}
            <Button size="sm" onClick={() => setEditing(true)}>
              <Pencil /> Edytuj
            </Button>
          </div>
        }
      />

      <ConfirmDialog
        open={lifecycle === 'archive'}
        onOpenChange={(open) => {
          if (!open) setLifecycle(null)
        }}
        title="Archiwizacja szkoły"
        description={
          <span className="flex flex-col gap-2">
            <span>Po archiwizacji szkoły:</span>
            <span className="flex flex-col gap-1 text-sm">
              <span>
                • zniknie z domyślnego widoku listy szkół (pozostanie pod filtrem statusu),
              </span>
              <span>
                • powiązane konta uczniów i nauczycieli stracą dostęp do funkcji wymagających
                aktywnej szkoły,
              </span>
              <span>• dane szkoły, klas i kont zostaną zachowane — nic nie jest usuwane,</span>
              <span>• operacja jest odwracalna — szkołę można przywrócić.</span>
            </span>
          </span>
        }
        confirmLabel="Archiwizuj"
        pending={pending}
        error={lifecycleError}
        onConfirm={runLifecycle}
      />

      <ConfirmDialog
        open={lifecycle === 'restore'}
        onOpenChange={(open) => {
          if (!open) setLifecycle(null)
        }}
        title="Przywrócenie szkoły"
        description="Szkoła wróci do stanu „aktywna” i ponownie pojawi się w domyślnym widoku listy."
        confirmLabel="Przywróć"
        pending={pending}
        error={lifecycleError}
        onConfirm={runLifecycle}
      />
      <div className="grid max-w-4xl gap-6 p-6 lg:grid-cols-2">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Logo</CardTitle>
          </CardHeader>
          <CardContent>
            <LogoUpload schoolId={school.id} logo={school.logo ?? null} />
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="size-4" /> Klasy
            </CardTitle>
            <Button asChild variant="outline" size="sm">
              <Link to="/klasy" search={{ school: school.id }}>
                Zobacz klasy
              </Link>
            </Button>
          </CardHeader>
          <CardContent className="flex gap-8 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Klasy aktywne</p>
              <p className="mt-0.5 text-2xl font-semibold tabular-nums">
                {school.active_class_count ?? '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Klasy łącznie</p>
              <p className="mt-0.5 text-2xl font-semibold tabular-nums">
                {school.total_class_count ?? '—'}
              </p>
            </div>
          </CardContent>
        </Card>

        {isOperator && school.state !== 'deleted' && (
          <BulkScopeActions
            scopeKind="school"
            scopeId={school.id}
            scopeName={school.name}
            schoolId={school.id}
            canMassDisable={isOperator}
            canCascade={isSuperadmin}
            massDisableAvailable={school.state === 'active'}
            cascadeAvailable={school.state === 'active' || school.state === 'archived'}
            onInitiateMassDisable={() => bulkDisableMutation.mutateAsync({ id })}
            onInitiateCascade={(data) => anonymizeMutation.mutateAsync({ id, data })}
            onCascadeCompleted={async () => {
              await queryClient.invalidateQueries({ queryKey: getV1SchoolsRetrieveQueryKey(id) })
              await queryClient.invalidateQueries({ queryKey: getV1SchoolsListQueryKey() })
            }}
          />
        )}

        <SchoolTeachersCard
          schoolId={school.id}
          schoolActive={school.state === 'active'}
          teachers={school.teachers}
        />

        <ModuleConfigCard
          level="school"
          targetId={school.id}
          targetState={school.state}
          role={role}
        />

        <Card>
          <CardHeader>
            <CardTitle>Identyfikacja</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            <Row label="Kod szkoły" value={school.school_code} />
            <Row label="Nazwa skrócona" value={school.short_name || '—'} />
            <Row label="Typ szkoły" value={school.school_type_name} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Dane operacyjne</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            <Row label="Województwo" value={school.voivodeship_label} />
            <Row label="Miejscowość" value={school.city} />
            <Row label="Adres" value={school.address} />
            <Row label="Data dołączenia" value={school.joined_program_on} />
            <Row
              label="Strona WWW"
              value={
                school.website ? (
                  <a
                    href={school.website}
                    target="_blank"
                    rel="noreferrer"
                    className="text-brand hover:underline"
                  >
                    {school.website}
                  </a>
                ) : (
                  '—'
                )
              }
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Zespolenie (RSPO)</CardTitle>
          </CardHeader>
          <CardContent>
            {school.rspo_link ? (
              <RspoZespolenie link={school.rspo_link} currentSchoolId={school.id} />
            ) : (
              <p className="text-sm text-muted-foreground">
                Szkoła nie jest powiązana z rejestrem RSPO. Powiąż ją w edycji, żeby zobaczyć
                pozostałe jednostki zespołu.
              </p>
            )}
          </CardContent>
        </Card>

        {isOperator && <SchoolPendingProposals schoolId={school.id} />}
        {isOperator && <SchoolProposalHistory schoolId={school.id} />}

        <Card>
          <CardHeader>
            <CardTitle>Kontakty</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            <ContactBlock title="Kontakt główny" contact={contact1} />
            {contact2 && <ContactBlock title="Kontakt dodatkowy" contact={contact2} />}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Notatki administracyjne</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">
              {school.admin_notes || '—'}
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[10rem_1fr] gap-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span>{value}</span>
    </div>
  )
}

function ContactBlock({
  title,
  contact,
}: {
  title: string
  contact?: { full_name: string; position: string; email: string; phone?: string }
}) {
  if (!contact) return <p className="text-sm text-muted-foreground">{title}: —</p>
  return (
    <div className="grid gap-1 text-sm">
      <p className="font-medium">{title}</p>
      <p>
        {contact.full_name} <span className="text-muted-foreground">· {contact.position}</span>
      </p>
      <p className="text-muted-foreground">{contact.email}</p>
      {contact.phone && <p className="text-muted-foreground">{contact.phone}</p>}
    </div>
  )
}
